import React from 'react';
import { Box } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomPagination from '@ui-components/Table/CustomPagination';
import EditOnlyIcon from '@assets/images/editOnly.svg';
import ClinicianUserIcon from '@assets/images/clinicianUser.svg';
import type { Facility } from '../../API/services/FacilityService';
import CustomLink from '@qler-view/shared_lib/src/shared/lib/ui-components/Link/CustomLink';
import { hasRole } from '../login/storageUtility';
import { formatFieldValue } from '@qler-view/shared_lib/src/reusableFunctions/CommonFunctions';

interface FacilityTableProps {
  isLoading: boolean;
  facility: Facility[];
  totalRecords: number;
  page: number;
  rowsPerPage: number;
  sortField: string;
  sortType: 'asc' | 'desc';
  action?: string;

  onSort: (field: string) => void;
  onPageChange: (page: number) => void;
  onEdit?: (id: number) => void;
  onFacilityCount?: (id: number, name: string | null) => void;

  getSortIcon: (type: 'asc' | 'desc') => React.ReactNode;
  getStartItem: (page: number, size: number) => number;
  getEndItem: (page: number, size: number, total: number) => number;
  onView?: (id: number) => void;
}
const FacilityTable: React.FC<FacilityTableProps> = ({
  isLoading,
  facility,
  totalRecords,
  page,
  rowsPerPage,
  sortField,
  sortType,
  action,
  onSort,
  onPageChange,
  onEdit,
  onFacilityCount,
  getSortIcon,
  getStartItem,
  getEndItem,
  onView,
}) => {
  return (
    <>
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CustomTypography id="loading" variant="h5" color="textDisabled">
            Loading...
          </CustomTypography>
        </Box>
      ) : (
        <CustomTable id="table-facility-user">
          <CustomTableHead id="table-head-facility-user">
            <CustomTableRow id="facility-user-header">
              <CustomTableCell
                id="table-cell-facility-PF-id"
                onClick={() => onSort('facilityBusinessId')}
              >
                PF ID&nbsp;
                {sortField === 'facilityBusinessId' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-facility-name"
                onClick={() => onSort('facilityName')}
              >
                FACILITY NAME &nbsp;
                {sortField === 'facilityName' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-partner-name"
                onClick={() => onSort('partnerName')}
              >
                PARTNER NAME &nbsp;
                {sortField === 'partnerName' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-facility-services-provided"
                onClick={() => onSort('serviceProvider')}
              >
                SERVICES PROVIDED &nbsp;
                {sortField === 'serviceProvider' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-created-on"
                onClick={() => onSort('createdOn')}
              >
                CREATED ON &nbsp;
                {sortField === 'createdOn' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-facility-status"
                onClick={() => onSort('status')}
              >
                STATUS &nbsp;
                {sortField === 'status' && getSortIcon(sortType)}
              </CustomTableCell>
              {action === 'list' && (
                <CustomTableCell id="table-facility-actions">
                  ACTIONS
                </CustomTableCell>
              )}
              {hasRole(['ops-admin']) && action === 'mapping' && (
                <CustomTableCell id="table-facility-actions">
                  ACTIONS
                </CustomTableCell>
              )}
            </CustomTableRow>
          </CustomTableHead>
          {facility && facility?.length > 0 ? (
            <CustomTableBody id="table-body-facility">
              {facility.map((facilities) => (
                <CustomTableRow
                  key={facilities.id}
                  id={`table-row-facility-${facilities.id}`}
                >
                  <CustomTableCell
                    id={`table-cell-facility-id-${facilities.id}`}
                  >
                    <CustomLink
                      id={`link-facility-id-${facilities.id}`}
                      color="primary"
                      onClick={() => onView?.(facilities.id)}
                    >
                      {facilities.facilityBusinessId}
                    </CustomLink>
                  </CustomTableCell>
                  <CustomTableCell
                    id={`table-cell-facility-name-${facilities.id}`}
                  >
                    {facilities.facilityName || 'N/A'}
                  </CustomTableCell>
                  <CustomTableCell
                    id={`table-cell-partner-name-${facilities.id}`}
                  >
                    {facilities.partnerName || 'N/A'}
                  </CustomTableCell>
                  <CustomTableCell
                    id={`table-cell-facility-alias-${facilities.id}`}
                  >
                    {facilities.serviceProvider || 'N/A'}
                  </CustomTableCell>

                  <CustomTableCell
                    id={`table-cell-created-on-${facilities.id}`}
                  >
                    {formatFieldValue(facilities.createdOn)}
                  </CustomTableCell>
                  <CustomTableCell
                    id={`table-cell-facility-status-${facilities.id}`}
                  >
                    {facilities.status}
                  </CustomTableCell>
                  {action === 'list' && (
                    <CustomTableCell
                      id={`table-cell-partner-action-${facilities.id}`}
                      className="width-10"
                    >
                      <Box className="flex align-items-center">
                        <Box
                          className="flex align-items-center"
                          onClick={() =>
                            onFacilityCount?.(
                              facilities.id,
                              facilities.facilityName
                            )
                          }
                        >
                          <CustomIconButton
                            id="user-icon"
                            size="small"
                            icon={<ClinicianUserIcon />}
                            onClick={() => {}}
                          />
                          <Box className="display-count cursor-pointer">
                            {facilities.clinicianCount}
                          </Box>
                        </Box>
                        {hasRole(['ops-admin']) && (
                          <Box className="ml-16">
                            <CustomIconButton
                              id="edit-icon"
                              size="small"
                              icon={<EditOnlyIcon />}
                              onClick={() => onEdit?.(facilities.id)}
                            />
                          </Box>
                        )}
                      </Box>
                    </CustomTableCell>
                  )}
                  {hasRole(['ops-admin']) && action === 'mapping' && (
                    <CustomTableCell id="table-facility-actions">
                      <CustomIconButton
                        id="table-facility-edit-button"
                        size="small"
                        icon={<EditOnlyIcon />}
                        onClick={() => onEdit?.(Number(facilities.id))}
                      />
                    </CustomTableCell>
                  )}
                </CustomTableRow>
              ))}
            </CustomTableBody>
          ) : (
            <CustomTableBody id="table-body-facility">
              <CustomTableRow id="table-row-facility">
                <CustomTableCell id="table-cell-facility-id" colspan={7}>
                  <Box className="flex flex-justify-center align-items-center">
                    <CustomTypography
                      id="no-facilities-found"
                      variant="body2"
                      color="textDisabled"
                    >
                      No Facilities Found
                    </CustomTypography>
                  </Box>
                </CustomTableCell>
              </CustomTableRow>
            </CustomTableBody>
          )}
        </CustomTable>
      )}
      {totalRecords > 0 && (
        <Box className="mt-32">
          <Grid2 container className="align-items-center">
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <Box className="flex justify-content-start">
                <CustomTypography
                  id="showing-row-count-text"
                  variant="body2"
                  color="textDisabled"
                >
                  Showing {getStartItem(page, rowsPerPage)} -{' '}
                  {getEndItem(page, rowsPerPage, totalRecords)} of{' '}
                  {totalRecords} Facilities
                </CustomTypography>
              </Box>
            </Grid2>
            {totalRecords > rowsPerPage && (
              <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                <Box display="flex" justifyContent="end">
                  <CustomPagination
                    id="facility-list-pagination"
                    count={Math.ceil(totalRecords / rowsPerPage)}
                    page={page + 1}
                    onPageChange={(_, value) => onPageChange(value - 1)}
                    color="standard"
                  />
                </Box>
              </Grid2>
            )}
          </Grid2>
        </Box>
      )}
    </>
  );
};

export default FacilityTable;
